/**
 * Lightweight client-side shopping cart (varukorg).
 *
 * The cart is deliberately NOT server-persisted — it lives in
 * localStorage so it works for logged-out visitors too and stays a
 * throw-away "things I want to buy" holding area. Checkout is always
 * single-vendor: the buyer takes one seller-group to checkout at a time.
 *
 * Each stored item carries just enough to render the flyout without a
 * network round-trip (title, price, thumbnail, seller) plus the
 * orderData the buyer had selected on the listing page (quantity /
 * delivery method / add-on services) so "Köp nu" can replay it into
 * the existing checkout.
 *
 * Cross-component updates are broadcast via a custom window event so
 * the topbar badge and the flyout re-render the moment an item is
 * added or removed — and the native `storage` event keeps other tabs
 * in sync.
 */

import { trackAddToCart, toMajorUnits } from './metaPixel';
import { packagePartsOf } from './packageBundleCore';
import { clampQuantity, lineLimits } from './orderQuantity';
import { cleanListingTitle } from './listingTitle';
import {
  buildItem,
  trackAddToCart as trackGa4AddToCart,
  trackRemoveFromCart as trackGa4RemoveFromCart,
} from './ecommerceEvents';

export const CART_STORAGE_KEY = 'wp_cart';
const CART_EVENT = 'wp:cartchange';

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const read = () => {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // Corrupt payload — treat as empty rather than crashing the topbar.
    return [];
  }
};

const write = items => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // Quota / privacy mode — silently ignore, the cart just won't persist.
  }
  // Notify same-tab listeners (the native `storage` event only fires in
  // OTHER tabs, so we dispatch our own for this one).
  window.dispatchEvent(new CustomEvent(CART_EVENT));
};

/**
 * Best-effort thumbnail URL from a denormalised listing entity.
 * Prefers a small square/card variant, falls back to the first variant.
 */
export const getListingThumb = listing => {
  const firstImage = listing?.images?.[0];
  const variants = firstImage?.attributes?.variants;
  if (!variants) return null;
  const preferred =
    variants['listing-card'] ||
    variants['square-small'] ||
    variants['default'] ||
    Object.values(variants)[0];
  return preferred?.url || null;
};

/** Build a storable cart item from a denormalised listing + selected orderData. */
export const buildCartItem = (listing, orderData = {}) => {
  const price = listing?.attributes?.price;
  const authorProfile = listing?.author?.attributes?.profile;
  /* What a Bygg paket package contains, stored with the line so the flyout can
   * show the sub-rows without a network call (P25009-403, BR-1). Display only —
   * checkout re-reads the parts from the listing server-side, so a stale or
   * edited copy here can never affect price or stock. */
  const packageParts = packagePartsOf(listing);
  /* P25009-474 BR-3/BR-4 — the stepper's range (stock, or one locked set) and
   * the unit noun, stored with the line so the mini cart can offer a stepper
   * without a network call. A snapshot: the cart page refreshes it from the
   * live listing (refreshCartLimits), and checkout prices against live stock. */
  const limits = lineLimits(
    listing?.attributes?.publicData || {},
    listing?.currentStock?.attributes?.quantity
  );
  return {
    listingId: listing?.id?.uuid,
    // 18"" → 18" (P25009-486 BR-4) — the cart and checkout read this.
    title: cleanListingTitle(listing?.attributes?.title),
    price: price ? { amount: price.amount, currency: price.currency } : null,
    image: getListingThumb(listing),
    authorId: listing?.author?.id?.uuid || null,
    authorName: authorProfile?.displayName || '',
    ...(packageParts.length > 0 ? { packageParts } : {}),
    limits,
    orderData: {
      quantity: clampQuantity(orderData.quantity || 1, limits),
      ...(orderData.deliveryMethod ? { deliveryMethod: orderData.deliveryMethod } : {}),
      ...(Array.isArray(orderData.selectedServiceIds) && orderData.selectedServiceIds.length > 0
        ? { selectedServiceIds: orderData.selectedServiceIds }
        : {}),
    },
    addedAt: Date.now(),
  };
};

export const getCart = () => read();

export const getCartCount = () => read().length;

export const isInCart = listingId => read().some(i => i.listingId === listingId);

/**
 * Add (or replace, keyed by listingId) an item. `item` should be built
 * with buildCartItem. No-op if it has no listingId.
 */
export const addToCart = item => {
  if (!item?.listingId) return read();
  const items = read().filter(i => i.listingId !== item.listingId);
  items.push(item);
  write(items);

  // Every route into the cart goes through here, so this is the one place
  // that can report the event without being forgotten by a future caller.
  const unitPrice = toMajorUnits(item.price?.amount);
  const quantity = item.orderData?.quantity || 1;

  trackAddToCart({
    listingId: item.listingId,
    title: item.title,
    value: unitPrice,
    currency: item.price?.currency,
  });

  trackGa4AddToCart({
    items: [buildItem({ listingId: item.listingId, title: item.title, price: unitPrice, quantity })],
    value: unitPrice != null ? unitPrice * quantity : null,
    currency: item.price?.currency,
  });

  return items;
};

export const removeFromCart = listingId => {
  const current = read();
  const removed = current.find(i => i.listingId === listingId);
  const items = current.filter(i => i.listingId !== listingId);
  write(items);

  // Only the buyer's own "remove" reports remove_from_cart. removeManyFromCart
  // below clears the cart after a completed order, which is not a removal the
  // shopper made and must not look like abandonment in the funnel.
  if (removed) {
    const unitPrice = toMajorUnits(removed.price?.amount);
    const quantity = removed.orderData?.quantity || 1;
    trackGa4RemoveFromCart({
      items: [
        buildItem({
          listingId: removed.listingId,
          title: removed.title,
          price: unitPrice,
          quantity,
        }),
      ],
      value: unitPrice != null ? unitPrice * quantity : null,
      currency: removed.price?.currency,
    });
  }

  return items;
};

export const clearCart = () => {
  write([]);
  return [];
};

/**
 * Remove several items at once (single write). Used after a successful order to
 * clear exactly the purchased listings from the cart — primary + bundled.
 */
export const removeManyFromCart = listingIds => {
  const ids = new Set((Array.isArray(listingIds) ? listingIds : []).filter(Boolean));
  if (ids.size === 0) return read();
  const items = read().filter(i => !ids.has(i.listingId));
  write(items);
  return items;
};

/**
 * Update the stored quantity for a cart line (the mini cart and cart page
 * steppers). Kept inside the line's stored limits: never above stock, and a
 * set line stays at one set (P25009-474 BR-3/BR-4).
 */
export const updateCartQuantity = (listingId, quantity) => {
  const items = read().map(i =>
    i.listingId === listingId
      ? {
          ...i,
          orderData: { ...(i.orderData || {}), quantity: clampQuantity(quantity, i.limits) },
        }
      : i
  );
  write(items);
  return items;
};

/**
 * Replace the stored limits with ones read from live listings, and pull each
 * quantity back inside them (stock may have dropped since the line was added).
 * Writes only when something changed, so a subscriber that calls this on every
 * cart change cannot loop.
 *
 * @param {Object} limitsById { [listingId]: lineLimits(...) }
 */
export const refreshCartLimits = limitsById => {
  const fresh = limitsById || {};
  let changed = false;
  const items = read().map(i => {
    const limits = fresh[i.listingId];
    if (!limits) return i;
    const quantity = clampQuantity(i.orderData?.quantity || 1, limits);
    if (
      JSON.stringify(limits) === JSON.stringify(i.limits) &&
      quantity === i.orderData?.quantity
    ) {
      return i;
    }
    changed = true;
    return { ...i, limits, orderData: { ...(i.orderData || {}), quantity } };
  });
  if (changed) write(items);
  return items;
};

/**
 * Group cart items by seller (authorId). Returns an array of
 * { authorId, authorName, items } preserving first-seen order — checkout
 * is per seller-group, so the UI iterates these groups.
 */
export const groupCartByVendor = (items = read()) => {
  const groups = [];
  const byId = new Map();
  items.forEach(item => {
    const key = item.authorId || 'unknown';
    let group = byId.get(key);
    if (!group) {
      group = { authorId: item.authorId || null, authorName: item.authorName || '', items: [] };
      byId.set(key, group);
      groups.push(group);
    }
    group.items.push(item);
  });
  return groups;
};

/**
 * Subscribe to cart changes (same tab via custom event, other tabs via
 * `storage`). Returns an unsubscribe function.
 */
export const subscribeToCart = callback => {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback(read());
  const storageHandler = e => {
    if (e.key === CART_STORAGE_KEY) callback(read());
  };
  window.addEventListener(CART_EVENT, handler);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(CART_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
};
